import PropTypes from 'prop-types';
// @mui
import { useTheme } from '@mui/material/styles';
import { Stack, Box, AppBar, Toolbar, IconButton, Button, Typography } from '@mui/material';
// utils
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { bgBlur } from '../../../utils/cssStyles';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// config
import { HEADER, NAV } from '../../../config';
// components
import Logo from '../../../components/logo';
import Iconify from '../../../components/iconify';
import { useSettingsContext } from '../../../components/settings';
import { useAuthContext } from '../../../auth/useAuthContext';
import Account from '../../../sections/auth/Account'
import SupabaseAuth from '../../../sections/auth/SupabaseAuth'
//
import Searchbar from './Searchbar';
import AccountPopover from './AccountPopover';
import LanguagePopover from './LanguagePopover';
import ContactsPopover from './ContactsPopover';
import NotificationsPopover from './NotificationsPopover';
import { StyledCard, StyledWrap, MaskControl } from '../../../components/settings/styles';
import SvgColor from '../../../components/svg-color/SvgColor';
import NavSectionHorizontal from '../../../components/nav-section/horizontal/NavSectionHorizontal';
import navConfig from '../nav/config';
import OmniSearch from '../../../components/search/OmniSearch';
import { useFilterUrl } from '../../../hooks/useFilterUrl';


// ----------------------------------------------------------------------

Header.propTypes = {
  onOpenNav: PropTypes.func,
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
  session: PropTypes.object,
  sidebarWidth: PropTypes.number,
  isResizing: PropTypes.bool,
};

export default function Header({ onOpenNav, isCollapsed, onToggleCollapse, session: sessionProp, sidebarWidth, isResizing }) {

  const { session: authSession, logout } = useAuthContext();
  const session = sessionProp || authSession;
  const [filterModel, setFilterModel] = useFilterUrl();
  

  const currentLocation = useLocation();

  const theme = useTheme();

  const { themeLayout } = useSettingsContext();

  // Force vertical layout for authenticated users to show sidebar
  const isNavHorizontal = !session && themeLayout === 'horizontal';

  const isNavMini = themeLayout === 'mini';

  const isDesktop = useResponsive('up', 'lg');

  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP) && !isNavHorizontal;

  const { themeMode, onChangeMode } = useSettingsContext();

  const navigate = useNavigate();
  const routeChange = () => {
    const path = `/login`;
    navigate(path);
  }

  const logoutfunc = async () => {
    console.log('🔗 [Header] Logout function called');
    try {
      await logout();
      console.log('🔗 [Header] Logout successful, navigating to /home');
      navigate('/home');
    } catch (error) {
      console.error('🚨 [Header] Logout error:', error);
    }
  };

  const handleThemeToggle = () => {
    const event = {
      target: {
        name: 'themeMode',
        value: themeMode === 'light' ? 'dark' : 'light'
      }
    };
    onChangeMode(event);
  };

  const renderContent = (
    <>
      {/* Mobile: hamburger | logo | gap | sign in | theme */}
      {!isDesktop ? (
        <>
          {/* Left side: hamburger and logo */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={onOpenNav} sx={{ color: 'text.primary' }}>
              <Iconify icon="eva:menu-2-fill" sx={{ color: 'text.secondary' }} />
            </IconButton>
            {!session && <Logo sx={{ mr: 0, ml: 0 }} />}
          </Stack>

          {/* Center: Search bar for authenticated users */}
          {session && (
            <Box flexGrow={1} mx={1}>
              <OmniSearch 
                size="small" 
                filterModel={filterModel}
                setFilterModel={setFilterModel}
              />
            </Box>
          )}

          {/* Spacer for non-authenticated */}
          {!session && <Box flexGrow={1} />}

          {/* Right side: sign in and theme */}
          <Stack direction="row" alignItems="center" spacing={1}>
            {!session ? (
              <>
                <Button
                  component={RouterLink}
                  to="/signin"
                  color="primary"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderRadius: '50px',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  Sign in
                </Button>
                <IconButton onClick={handleThemeToggle}>
                  <SvgColor 
                    src={`/assets/icons/setting/ic_${themeMode === 'light' ? 'sun' : 'moon'}.svg`}
                    sx={{ width: 20, height: 20 }}
                  />
                </IconButton>
              </>
            ) : (
              <AccountPopover />
            )}
          </Stack>
        </>
      ) : (
        /* Desktop layout */
        <>
          {session ? (
            // Authenticated user: show search bar without logo
            <>
              {/* Sidebar toggle button */}
              <IconButton 
                onClick={onToggleCollapse || (() => {})} 
                sx={{ mr: 1 }}
                disabled={!onToggleCollapse}
              >
                <Iconify 
                  icon="mynaui:sidebar" 
                  width={20} 
                  height={20} 
                  sx={{ 
                    transform: isCollapsed ? 'rotate(180deg)' : 'none', 
                    transition: 'transform 0.2s' 
                  }} 
                />
              </IconButton>
              {/* Search bar in center */}
              <Box flexGrow={1} display="flex" justifyContent="center" maxWidth="600px" mx={2}>
                <OmniSearch 
                  size="medium" 
                  filterModel={filterModel}
                  setFilterModel={setFilterModel}
                />
              </Box>
              {/* Spacer */}
              <Box flexGrow={1} />
            </>
          ) : (
            // Non-authenticated user: show logo and nav links
            <>
              {/* Logo on the left */}
              <Logo sx={{ mr: 2 }} />
              {/* Centered horizontal nav links */}
              <Box flexGrow={1} display="flex" justifyContent="center">
                <NavSectionHorizontal data={navConfig} />
              </Box>
            </>
          )}

          {/* Action buttons */}
          <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
            {!session ? (
              <>
                <Button
                  component={RouterLink}
                  to="/signin"
                  color="primary"
                  variant="outlined"
                  size="large"
                  sx={{
                    mb: 2,
                    position: 'relative',
                    paddingRight: 3,
                    borderRadius: '50px',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  Sign in
                </Button>
                <IconButton onClick={handleThemeToggle} sx={{ ml: 1 }}>
                  <SvgColor 
                    src={`/assets/icons/setting/ic_${themeMode === 'light' ? 'sun' : 'moon'}.svg`}
                    sx={{ width: 20, height: 20 }}
                  />
                </IconButton>
              </>
            ) : (
              <AccountPopover />
            )}
          </Stack>
        </>
      )}
    </>
  );

  return (
    <AppBar
      sx={{
        boxShadow: 'none',
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: isResizing 
          ? 'none'
          : theme.transitions.create(['height', 'width'], {
              duration: theme.transitions.duration.shorter,
            }),
        ...(isDesktop && {
          width: session
            ? 'calc(100% - var(--sidebar-width, 280px) - 1px)'
            : `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
          height: HEADER.H_DASHBOARD_DESKTOP,
          ...(isOffset && {
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
            borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}
